/**
 * Draft line paint. Unstyled lines keep the road-band color, or gray when
 * the span is plain (no work area, or no road match). A saved style wins.
 *
 * Filters must be expression filters. A legacy `$type` check makes MapLibre
 * validate the whole filter as a legacy filter, reject `["get", "pattern"]`,
 * and drop the layer. The pick overlay would be the only line left on the map.
 */

export const DRAFT_LINE_OPACITY = 0.95;
/** Decorative segments (장식선) visible in the route editor during stroke preview. */
export const DECORATIVE_LINE_OPACITY = 0.48;

/**
 * Round caps and joins so separate segment features meet without the light
 * butt-cap gaps that showed once the white casing stroke was removed.
 */
export const ROUTE_LINE_LAYOUT = {
  "line-cap": "round",
  "line-join": "round",
} as const;

/** Gray used when a line has no road band and no saved stroke. */
export const PLAIN_LINE_COLOR = "#6b7280";

export const DRAFT_LINE_COLOR = [
  "coalesce",
  ["get", "stroke"],
  [
    "match",
    ["get", "band"],
    "on",
    "#1f9d55",
    "near",
    "#e0a100",
    "far",
    "#d7263d",
    "plain",
    PLAIN_LINE_COLOR,
    PLAIN_LINE_COLOR,
  ],
];

/** Missing width is the built-in 4.5 px stroke, not zero. */
export const DRAFT_LINE_WIDTH = ["coalesce", ["get", "width"], 4.5];

/** Drop shadow under route strokes (fills stay below this). */
export const ROUTE_LINE_SHADOW_COLOR = "#000000";
export const ROUTE_LINE_SHADOW_OPACITY = 0.3;
export const ROUTE_LINE_SHADOW_BLUR = 3;
export const ROUTE_LINE_SHADOW_WIDTH = ["+", ["coalesce", ["get", "width"], 4.5], 3];
export const ROUTE_LINE_SHADOW_TRANSLATE: [number, number] = [0, 2];

export const ROUTE_LINE_SHADOW_PAINT = {
  "line-color": ROUTE_LINE_SHADOW_COLOR,
  "line-opacity": ROUTE_LINE_SHADOW_OPACITY,
  "line-blur": ROUTE_LINE_SHADOW_BLUR,
  "line-width": ROUTE_LINE_SHADOW_WIDTH,
  "line-translate": ROUTE_LINE_SHADOW_TRANSLATE,
} as const;

const LINE_STRING = ["==", ["geometry-type"], "LineString"];

const NOT_DECORATIVE = ["!=", ["coalesce", ["get", "decorative"], 0], 1];

/** Solid, missing, or null pattern. Dashed and dotted stay on their own layers. */
export const SOLID_LINE_FILTER = [
  "all",
  LINE_STRING,
  NOT_DECORATIVE,
  ["!", ["in", ["coalesce", ["get", "pattern"], "solid"], ["literal", ["dashed", "dotted"]]]],
];

export const DASHED_LINE_FILTER = [
  "all",
  LINE_STRING,
  NOT_DECORATIVE,
  ["==", ["coalesce", ["get", "pattern"], "solid"], "dashed"],
];

export const DOTTED_LINE_FILTER = [
  "all",
  LINE_STRING,
  NOT_DECORATIVE,
  ["==", ["coalesce", ["get", "pattern"], "solid"], "dotted"],
];

export const DECORATIVE_LINE_FILTER = ["all", LINE_STRING, ["==", ["coalesce", ["get", "decorative"], 0], 1]];

/**
 * Route editor: unselected decorative lines are invisible; selection uses the pick
 * overlay. `strokePreview` keeps lines visible while 장식선 stroke mode is on.
 */
export const EDITOR_DECORATIVE_LINE_OPACITY = [
  "case",
  ["==", ["coalesce", ["get", "picked"], 0], 1],
  0,
  ["==", ["coalesce", ["get", "strokePreview"], 0], 1],
  DECORATIVE_LINE_OPACITY,
  0,
];

/** Invisible wide stroke for hit-testing unshown decorative lines in the editor. */
export const EDITOR_DECORATIVE_HIT_FILTER = [
  "all",
  DECORATIVE_LINE_FILTER,
  ["==", ["coalesce", ["get", "picked"], 0], 0],
  ["==", ["coalesce", ["get", "strokePreview"], 0], 0],
];

export const EDITOR_DECORATIVE_HIT_WIDTH = ["+", ["coalesce", ["get", "width"], 4.5], 12];
